import validateCreatives from '@/editor/services/creatives/creatives-validator.service';
import { AvailablePlatformsEnum, PlatformType } from '@/editor/shared/constants/available-platforms';
import {
  selectActivePlatforms,
  selectCreativeFormsState,
  selectCreativeSubmissionProgress,
  selectGoogleCroppedDataUrls,
  selectSelectedCreativeType,
} from '@/editor/store/selectors/creative.selectors';
import { useAppDispatch, useAppSelector } from '@/store/hooks/redux';
import { useCallback, useEffect, useMemo } from 'react';
import { useCreativesService } from './useCreativeService';
import { getValueFromLs, setKeyValuePairToLS } from '@/shared/services/localstorage';
import { EDITOR_LS_CONSTANTS } from '@/editor/shared/constants/editor-LS.constants';
import { useMobileImage } from '@/editor/components/media-library/images/hooks/useMobileImage';
import { setMobileMediaOnSingle } from '@/editor/store/reducers/creative-step.slice';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useCroppedGoogleImages } from '@/editor/components/media-library/images/hooks/useGoogleImages';
import { ExternalCompanyIdEnum } from '@/shared/constants/external-company-id.enum';

export const useSubmitCreatives = (handleChangeActiveStep: (step: number) => void) => {
  const renderedFormSubmissionState = useAppSelector(selectCreativeSubmissionProgress);
  const creativeFormsState = useAppSelector(selectCreativeFormsState);
  const platformList = useAppSelector(selectActivePlatforms);
  const selectedCreativeType = useAppSelector(selectSelectedCreativeType);
  const propertyInternalId = useMemo(() => getValueFromLs(EDITOR_LS_CONSTANTS.PROPERTY_INTERNAL_ID) as string, []);
  const companyId = useAppSelector((state) => state.propertyInfoSliceReducer.companyId);

  const selectedGoogleImageData = useAppSelector(selectGoogleCroppedDataUrls);

  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { creativeService, isLoading } = useCreativesService();

  const { handleCropImage } = useMobileImage();

  const { uploadImages } = useCroppedGoogleImages(selectedGoogleImageData, propertyInternalId);

  const createMobileImage = async () => {
    const resultPromise = await toast.promise(handleCropImage(), {
      loading: t('uploadImage'),
      success: t('successfulProcessing'),
      error: t('somethingWrong'),
    });

    if (resultPromise.data !== null) {
      dispatch(setMobileMediaOnSingle({ mediaId: resultPromise.data, platform: AvailablePlatformsEnum.META }));
    }

    return resultPromise.data;
  };

  const savingAction = useCallback(async () => {
    if (
      !(await validateCreatives(
        creativeFormsState,
        platformList.filter((el: PlatformType) => el !== AvailablePlatformsEnum.DELTA),
        selectedCreativeType
      ))
    ) {
      return;
    }

    const mobileResponse = companyId === ExternalCompanyIdEnum.EMERA_EIE ? await createMobileImage() : null;

    let googleResponse;

    try {
      googleResponse = await toast.promise(uploadImages(), {
        loading: t('uploadImage'),
        success: t('successfulProcessing'),
        error: t('somethingWrong'),
      });
    } catch (error) {
      console.error('Failed to upload Google images:', error);
      toast.error(t('googleImageUploadFailed'));
      return;
    }

    const response = await creativeService.create(
      platformList,
      selectedCreativeType,
      creativeFormsState,
      mobileResponse,
      googleResponse
    );

    setKeyValuePairToLS(EDITOR_LS_CONSTANTS.CREATED_CREATIVES, JSON.stringify(response));

    handleChangeActiveStep(3);
  }, [creativeFormsState, renderedFormSubmissionState, selectedGoogleImageData]);

  useEffect(() => {
    if (!renderedFormSubmissionState.active) {
      return;
    }

    savingAction();
  }, [renderedFormSubmissionState.active]);

  return { isLoading };
};
